
import { useState } from 'react';
import Button from '../base/Button';

export default function Header() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState<string | null>(null);

  const navItems = [
    { label: 'Tentang M4CR', href: '/tentang' },
    { label: 'Peta Mangrove', href: '/peta-mangrove' },
    { 
      label: 'Layanan', 
      href: '#',
      submenu: [
        { label: 'Pengaduan', href: '/layanan/pengaduan' },
        { label: 'Katalog Produk', href: '/layanan/katalog-produk' },
        { label: 'Pengetahuan', href: '/layanan/pengetahuan' },
        { label: 'Monitoring Penanaman', href: '/layanan/monitoring' }
      ]
    },
    { 
      label: 'Hiburan', 
      href: '#',
      submenu: [
        { label: 'Tanam Mangrove', href: '/hiburan/tanam-mangrove' },
        { label: 'Dunia Mangrove (360)', href: '/hiburan/dunia-mangrove-360' }
      ]
    },
    { 
      label: 'Publikasi', 
      href: '#',
      submenu: [
        { label: 'Siaran Pers', href: '/publikasi/siaran-pers' },
        { label: 'Artikel', href: '/publikasi/artikel' },
        { label: 'Pengumuman', href: '/publikasi/pengumuman' },
        { label: 'Agenda', href: '/publikasi/agenda' }
      ]
    }
  ];

  const handleDropdownClick = (label: string) => {
    setActiveDropdown(activeDropdown === label ? null : label);
  };

  // Close dropdown when clicking outside
  const handleDocumentClick = (e: MouseEvent) => {
    const target = e.target as HTMLElement;
    if (!target.closest('.dropdown-container')) {
      setActiveDropdown(null);
    }
  };

  // Add event listener for clicking outside
  useState(() => {
    document.addEventListener('click', handleDocumentClick);
    return () => document.removeEventListener('click', handleDocumentClick);
  });

  return (
    <header className="bg-white shadow-sm border-b border-gray-200 sticky top-0 z-50">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <a href="/" className="text-3xl font-bold text-[#2D8B57] hover:text-[#2D8B57]/80 transition-colors duration-200" style={{ fontFamily: 'Pacifico, serif' }}>
                M4CR
              </a>
            </div>
          </div>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center space-x-8">
            {navItems.map((item) => (
              <div
                key={item.label}
                className="relative dropdown-container"
              >
                <a
                  href={item.submenu ? '#' : item.href}
                  onClick={(e) => {
                    if (item.submenu) {
                      e.preventDefault();
                      handleDropdownClick(item.label);
                    }
                  }}
                  className="text-[#333333] hover:text-[#2D8B57] transition-colors duration-200 font-medium text-lg flex items-center cursor-pointer"
                >
                  {item.label}
                  {item.submenu && (
                    <i className={`ri-arrow-down-s-line ml-1 text-sm transition-transform duration-200 ${
                      activeDropdown === item.label ? 'rotate-180' : ''
                    }`}></i>
                  )}
                </a>
                
                {/* Dropdown Menu */}
                {item.submenu && activeDropdown === item.label && (
                  <div className="absolute top-full left-0 mt-2 w-64 bg-white border border-gray-200 rounded-lg shadow-lg py-2 z-50 animate-in fade-in-0 zoom-in-95 duration-200">
                    {item.submenu.map((subItem) => (
                      <a
                        key={subItem.label}
                        href={subItem.href}
                        className="block px-4 py-3 text-[#333333] hover:text-[#2D8B57] hover:bg-gray-50 transition-colors duration-200"
                        onClick={() => setActiveDropdown(null)}
                      >
                        {subItem.label}
                      </a>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </nav>

          {/* CTA Button */}
          <div className="hidden lg:block">
            <Button 
              variant="primary" 
              size="lg"
              href="/program"
              className="bg-[#2D8B57] hover:bg-[#2D8B57]/90 px-6 py-3"
            >
              View Program
            </Button>
          </div>

          {/* Mobile menu button */}
          <button
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            className="lg-hidden p-2 rounded-md text-[#333333] hover:text-[#2D8B57] hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-[#2D8B57]"
          >
            <div className="w-6 h-6 flex items-center justify-center">
              <i className={`ri-${isMenuOpen ? 'close' : 'menu'}-line text-xl`}></i>
            </div>
          </button>
        </div>

        {/* Mobile Navigation */}
        {isMenuOpen && (
          <div className="lg:hidden py-4 border-t border-gray-200">
            <nav className="flex flex-col space-y-4">
              {navItems.map((item) => (
                <div key={item.label}>
                  <a
                    href={item.submenu ? '#' : item.href}
                    onClick={(e) => {
                      if (item.submenu) {
                        e.preventDefault();
                        handleDropdownClick(item.label);
                      } else {
                        setIsMenuOpen(false);
                      }
                    }}
                    className="text-[#333333] hover:text-[#2D8B57] transition-colors duration-200 font-medium py-2 text-lg flex items-center justify-between"
                  >
                    {item.label}
                    {item.submenu && (
                      <i className={`ri-arrow-down-s-line text-sm transition-transform duration-200 ${
                        activeDropdown === item.label ? 'rotate-180' : ''
                      }`}></i>
                    )}
                  </a>
                  
                  {/* Mobile Submenu */}
                  {item.submenu && activeDropdown === item.label && (
                    <div className="ml-4 mt-2 space-y-2 animate-in fade-in-0 slide-in-from-top-2 duration-200">
                      {item.submenu.map((subItem) => (
                        <a
                          key={subItem.label}
                          href={subItem.href}
                          className="block text-gray-600 hover:text-[#2D8B57] transition-colors duration-200 py-1"
                          onClick={() => {
                            setIsMenuOpen(false);
                            setActiveDropdown(null);
                          }}
                        >
                          {subItem.label}
                        </a>
                      ))}
                    </div>
                  )}
                </div>
              ))}
              <div className="pt-4">
                <Button 
                  variant="primary" 
                  size="lg" 
                  className="w-full bg-[#2D8B57] hover:bg-[#2D8B57]/90"
                  href="/program"
                >
                  View Program
                </Button>
              </div>
            </nav>
          </div>
        )}
      </div>
    </header>
  );
}
